'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { LuHeart } from 'react-icons/lu'
// 액션바 하트는 Phosphor 채운 아이콘 — 대화 · 공유와 같은 결(ui/action-icons)
import { PiHeartFill } from 'react-icons/pi'

import { prefersReducedMotion } from '@/core/lib/motion'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import WorkTitleOverlay from '@/features/veluvit/components/ui/work-title-overlay'
import { cn } from '@/core/utils/common'
import { useMediaQuery } from '@/core/hooks/use-media-query'

import {
  ChatIcon,
  ShareIcon,
} from '@/features/veluvit/components/ui/action-icons'
import { SkewTag } from '@/features/veluvit/components/ui/fx'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { isFresh, workStats } from '@/features/veluvit/lib/explore'
import { kfmt } from '@/features/veluvit/lib/format'
import { gsap } from '@/features/veluvit/lib/gsap'
import { IMAGE_QUALITY } from '@/features/veluvit/lib/image-quality'
import { to } from '@/features/veluvit/lib/route'
import { workFor } from '@/features/veluvit/lib/rules'
import { api } from '@/features/veluvit/services/api'
import { shareLink } from '@/features/veluvit/services/catalog.actions'

import type { MouseEvent } from 'react'

const RELATION =
  /친구|이웃|동기|선배|후배|동거|소꿉|상사|동료|짝사랑|계약|재회|주인|파트너|부부|라이벌|첫사랑/
const PERSONALITY =
  /능글|다정|무심|츤데레|쿨데레|집착|질투|장난|직진|수줍|순애|후회|소유|냉정|온화|시크|상처|갭모에|대형견|소심|까칠|싸가지|음침/

/* 우측 액션바 한 칸 — 아이콘 위, 숫자(없으면 이름) 아래. 버튼·링크 공용.
   모바일은 사진 위에 떠 있어 그림자로 읽히게, PC 는 카드 바깥(쇼츠처럼)이라 회색 원 버튼 */
const ACTION =
  'group flex w-12 cursor-pointer flex-col items-center gap-1 text-c12 leading-[1.2] font-semibold text-white no-underline tabular-nums [text-shadow:0_1px_4px_rgb(0_0_0/0.6)] over10:gap-1.5 over10:text-c13 over10:text-t2 over10:[text-shadow:none]'
/** 아이콘 칸 — 모바일은 원 없이 아이콘만(36px 칸), PC 는 회색 원(48px) */
const ACTION_ICON =
  'grid size-9 place-items-center rounded-full drop-shadow-[0_1px_6px_rgb(0_0_0/0.5)] transition-[scale,background-color] duration-200 ease-smooth group-active:scale-90 over10:size-12 over10:bg-foreground/[0.1] over10:text-white over10:drop-shadow-none over10:group-hover:bg-foreground/[0.18]'
/** 아이콘 자체 — 세 개 모두 같은 크기 */
const ACTION_GLYPH = 'size-8 over10:size-6'
/** 대화 · 공유는 살짝 투명하게 — 순백 면이 하트보다 튀었다(2026-10-08). 하트가 주인공 */
const ACTION_SOFT = 'opacity-85'

/** 두 번 톡 판정 — 이 안에 같은 자리를 다시 누르면 좋아요 */
const DOUBLE_MS = 300
const DOUBLE_PX = 36

type Burst = { id: number; x: number; y: number; tilt: number }

/** 두 번 톡 하트 — 누른 자리에서 튀어 올랐다 떠오르며 사라진다 */
function HeartBurst({
  burst,
  onDone,
}: {
  burst: Burst
  onDone: (id: number) => void
}) {
  const ref = useRef<HTMLSpanElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (prefersReducedMotion()) {
      const t = window.setTimeout(() => onDone(burst.id), 500)
      return () => window.clearTimeout(t)
    }
    const tl = gsap
      .timeline({ onComplete: () => onDone(burst.id) })
      .fromTo(
        el,
        { scale: 0, autoAlpha: 0, rotate: burst.tilt },
        { scale: 1.15, autoAlpha: 1, duration: 0.22, ease: 'back.out(2.4)' }
      )
      .to(el, { scale: 1, duration: 0.12, ease: 'power1.out' })
      .to(
        el,
        { y: -70, scale: 0.7, autoAlpha: 0, duration: 0.45, ease: 'power2.in' },
        '+=0.22'
      )
    return () => void tl.kill()
  }, [burst, onDone])
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute z-[4] -translate-1/2 opacity-0"
      style={{ left: burst.x, top: burst.y }}
    >
      <LuHeart
        className="size-24 fill-coral text-coral drop-shadow-[0_6px_18px_color-mix(in_srgb,var(--point)_45%,transparent)]"
        strokeWidth={1.2}
      />
    </span>
  )
}

type ReelSlideProps = {
  id: string
  index: number
  /** 지금 화면에 걸린 장 — 느린 확대·키보드 좋아요 대상 */
  active: boolean
  /** 걸린 장 앞뒤 — 사진을 미리 받는다 */
  near: boolean
  liked: boolean
  onLike: (id: string, favorite: boolean) => void
  /** 말풍선 — 댓글 열기(PC 패널 · 모바일 시트는 탐색 화면이 고른다) */
  onComments: (id: string) => void
  /** PC 댓글 패널이 이 장의 댓글을 보이는 중 */
  commentsOpen: boolean
}

/** 작품별 댓글 수 — 걸린 장이 될 때 한 번 받아 둔다(서버 목록 지표에 댓글 수가 생기면 그 값으로 바꾼다 — backend.md 17) */
const commentCounts = new Map<string, number>()

/**
 * 탐색 한 장 — 9:16 장면 사진 위에 캡션(이름·관계·소개)과 우측 액션바(좋아요 · 댓글 · 공유 · 상세).
 * 릴스처럼 사진을 두 번 톡 하면 좋아요(이미 좋아요면 유지), 캡션의 '더 보기'로 소개 전문과 대사·태그를 펼친다.
 */
export default function ReelSlide({
  id,
  index,
  active,
  near,
  liked,
  onLike,
  onComments,
  commentsOpen,
}: ReelSlideProps) {
  const { catalog, src, thumb } = useCatalogMedia()
  const commentsOff = workFor(catalog, id)?.commentsOn === false
  const [commentCount, setCommentCount] = useState<number | null>(
    () => commentCounts.get(id) ?? null
  )
  useEffect(() => {
    if (!active || commentsOff || commentCounts.has(id)) return
    let alive = true
    api<{ total: number }>(`/work/${id}/comments/?offset=0`)
      .then((page) => {
        commentCounts.set(id, page.total)
        if (alive) setCommentCount(page.total)
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [active, commentsOff, id])
  const [open, setOpen] = useState(false)
  // 다른 장으로 넘어가면 펼친 캡션을 접어 둔다 (렌더 중 상태 맞추기)
  if (!active && open) setOpen(false)
  const [bursts, setBursts] = useState<Burst[]>([])
  const lastTap = useRef<{ t: number; x: number; y: number } | null>(null)
  const seq = useRef(0)
  const heart = useRef<HTMLSpanElement>(null)

  const c = catalog.chars[id]
  const work = workFor(catalog, id)
  const reducedMotion =
    useMediaQuery('(prefers-reduced-motion: reduce)') && prefersReducedMotion()
  const stillCover = thumb(id) || src(id, index % 2 ? 's2' : 's1')
  const animatedCover = active && !reducedMotion ? work?.introGif : undefined
  const cast = work?.cast || []
  const lead = cast.find((p) => p.name === work?.name) || cast[0]
  const hook = c.hook || work?.description || ''
  const more = c.hookLong && c.hookLong !== hook ? c.hookLong : ''
  const relationRaw =
    work?.userRel ||
    lead?.userRel ||
    lead?.role ||
    (c.tags || [])
      .filter((t) => RELATION.test(t))
      .slice(0, 2)
      .join(' · ')
  // 관계 문구가 소개 문구와 같으면(대부분 lead.role = hook) 두 번 쓰지 않는다
  const relation = relationRaw && relationRaw !== hook ? relationRaw : ''
  const tags = Array.from(
    new Set([
      ...(c.tags || []).filter((t) => PERSONALITY.test(t)),
      ...(c.tags || []),
    ])
  ).slice(0, open ? 8 : 3)
  const adult = (c.badges || []).includes('19+')
  const stats = workStats(catalog, id)
  const fresh = isFresh(catalog, id)
  const expandable = !!(more || c.quote || (c.tags || []).length > 3)

  /** 좋아요로 바뀌는 순간 하트가 톡 튄다 */
  const pop = () => {
    if (!heart.current || prefersReducedMotion()) return
    gsap.fromTo(
      heart.current,
      { scale: 0.6 },
      { scale: 1, duration: 0.45, ease: 'back.out(3)', clearProps: 'scale' }
    )
  }
  const like = (favorite: boolean) => {
    if (favorite && !liked) pop()
    if (favorite !== liked) onLike(id, favorite)
  }

  const onStageClick = (e: MouseEvent<HTMLDivElement>) => {
    if (open) return setOpen(false)
    const box = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - box.left
    const y = e.clientY - box.top
    const prev = lastTap.current
    if (
      prev &&
      e.timeStamp - prev.t < DOUBLE_MS &&
      Math.hypot(x - prev.x, y - prev.y) < DOUBLE_PX
    ) {
      lastTap.current = null
      const burst = {
        id: ++seq.current,
        x,
        y,
        tilt: Math.round(Math.random() * 30 - 15),
      }
      setBursts((list) => [...list, burst])
      like(true)
      return
    }
    lastTap.current = { t: e.timeStamp, x, y }
  }
  const dropBurst = useCallback(
    (burstId: number) =>
      setBursts((list) => list.filter((b) => b.id !== burstId)),
    []
  )

  return (
    // 모바일 = 화면 가득 한 장(사진 위에 캡션 · 액션바). PC = 유튜브 쇼츠처럼 위아래 여백을 둔 9:16 둥근 카드 + 카드 오른쪽 바깥 액션바(2026-10-08)
    <section
      aria-label={c.name}
      aria-roledescription="슬라이드"
      data-reel={id}
      className="relative h-full overflow-hidden bg-black select-none over10:flex over10:items-end over10:justify-center over10:gap-4 over10:overflow-visible over10:bg-transparent over10:pt-2 over10:pb-6"
    >
      {/* PC 왼쪽 빈 칸 — 오른쪽 액션바(48px)와 같은 폭. [카드 + 액션바]를 통째로 가운데 두면 카드가 32px 왼쪽으로 밀려 위 탭과 어긋났다(2026-10-08) */}
      <div aria-hidden="true" className="hidden w-12 flex-none over10:block" />
      {/* 카드 — 모바일은 화면 그 자체, PC 는 높이에 맞춘 9:16 */}
      <div className="absolute inset-0 overflow-hidden over10:relative over10:inset-auto over10:aspect-[9/16] over10:h-full over10:max-w-[calc(100%-8rem)] over10:rounded-[20px] over10:bg-black over10:shadow-[0_24px_60px_-20px_rgb(0_0_0/0.8),inset_0_0_0_1px_rgb(255_255_255/0.06)]">
        {/* 사진 — 걸린 장만 아주 천천히 다가간다(살아 있는 화면). 사진 층을 누르는 것이 두 번 톡 좋아요 */}
        <div
          aria-hidden="true"
          onClick={onStageClick}
          className="absolute inset-0 cursor-default"
        >
          <SafeImage
            src={animatedCover || stillCover}
            fallbackSrc={stillCover}
            unoptimized={!!animatedCover}
            alt=""
            fill
            sizes="(min-width: 1001px) 520px, 100vw"
            preload={index === 0}
            loading={index === 0 ? undefined : near ? 'eager' : 'lazy'}
            quality={IMAGE_QUALITY.scene}
            className={cn(
              'object-cover transition-[scale] ease-out motion-reduce:transition-none',
              active ? 'scale-[1.07] duration-[14s]' : 'scale-100 duration-500'
            )}
            appearance="plain"
          />
          {/* 위는 탭 글씨, 아래는 캡션이 읽히게 — 캡션을 펼치면 더 어둡게 */}
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgb(0_0_0/0.55)_0,transparent_18%,transparent_42%,rgb(0_0_0/0.9)_100%)]" />
          <div
            className={cn(
              'absolute inset-0 bg-black/55 transition-opacity duration-300',
              open ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
        {bursts.map((b) => (
          <HeartBurst key={b.id} burst={b} onDone={dropBurst} />
        ))}

        {/* 캡션 — 이름·대화 시작 → 관계 → 소개(두 줄, 더 보기) → 태그. 모바일은 오른쪽 액션바 자리를 비우고, PC 는 액션바가 카드 밖이라 꽉 */}
        {/* 모바일 아래 여백 — 하단 탭을 숨겼으니(2026-10-08) 탭 자리(116px) 대신 기기 하단 안전 영역 + 24px */}
        <div className="absolute right-17 bottom-[calc(24px+env(safe-area-inset-bottom,0px))] left-4 z-[3] over10:right-4 over10:bottom-5 max-[359px]:right-14 max-[359px]:left-3">
          {catalog.images[id]?.logo && (
            <div className="relative mb-3 aspect-[3/1] w-3/4 max-w-72">
              <WorkTitleOverlay
                src={catalog.images[id].logo}
                inset
                sizes="288px"
              />
            </div>
          )}
          {/* 라벨 줄 — 신작 · 연령 · 대표 장르 태그 */}
          {(fresh || adult || tags[0]) && (
            <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
              {fresh && <SkewTag tone="point">NEW</SkewTag>}
              {adult && <SkewTag tone="glass">19</SkewTag>}
              {tags[0] && <SkewTag tone="glass">{tags[0]}</SkewTag>}
            </div>
          )}
          {/* 이름 — 옆 '대화 시작' 알약은 뺐다(2026-10-08). 대화는 오른쪽 액션바의 말풍선이 맡는다 */}
          {/* 모바일은 글자를 줄이고 두 줄까지 — 한 줄 말줄임(…)으로 제목이 잘리면 몰입이 깨진다(2026-10-08) */}
          <h2 className="line-clamp-2 min-w-0 text-c22 leading-[1.25] font-bold break-keep title-glow max-[359px]:text-c20 over10:text-c28">
            {c.name}
          </h2>
          <div className="mt-2 max-w-[420px] [text-shadow:0_1px_6px_rgb(0_0_0/0.7)]">
            {relation && (
              <div className="mb-1 line-clamp-1 text-c12 font-bold text-point">
                {relation}
              </div>
            )}
            {/* 펼치면 캡션만 스크롤 — 긴 소개가 액션바·탭을 덮지 않게 높이를 묶는다 */}
            <div
              className={cn(
                'text-c14 leading-[1.6] text-white',
                open &&
                  'scrollbar-none max-h-[42dvh] overflow-y-auto overscroll-contain'
              )}
            >
              <p className={cn('font-semibold', !open && 'line-clamp-2')}>
                {hook}
              </p>
              {open && more && (
                <p className="mt-2 whitespace-pre-line text-white/85">{more}</p>
              )}
              {open && c.quote && (
                <p className="mt-2.5 border-l-2 border-point/70 pl-2.5 text-c13 text-t2">
                  {c.quote}
                </p>
              )}
            </div>
            <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-c13">
              {tags.map((t) => (
                <span key={t} className="font-semibold text-white/80">
                  #{t}
                </span>
              ))}
              {expandable && (
                <Button
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpen((v) => !v)}
                  className="cursor-pointer font-semibold text-white/60 transition-colors hover:text-white focus-visible:text-white"
                  variant="bare"
                >
                  {open ? '접기' : '더 보기'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 우측 액션바 — 모바일은 사진 위 오른쪽 아래, PC 는 카드 오른쪽 바깥 아래(쇼츠).
          2026-10-08 다시 잡음: 순서 대화 수 → 좋아요 수 → 공유(이름 대신 숫자, 0 이어도 숫자), 아이콘은 사진 위에서 읽히는 채운 아이콘 한 벌(Phosphor Fill)로 같은 크기,
          아이콘 바로 밑에 숫자(간격 2px) — 예전 선 아이콘(뒤집은 말풍선 · 기울인 종이비행기)은 크기 · 축이 제각각이라 줄이 어긋났다 */}
      <div className="absolute right-2 bottom-[calc(24px+env(safe-area-inset-bottom,0px))] z-[3] flex flex-col items-center gap-4 over10:static over10:gap-4 over10:pb-1 max-[359px]:right-1">
        <Button
          type="button"
          aria-pressed={liked}
          aria-label={`${liked ? '좋아요 취소' : '좋아요'} · ${stats.likes}`}
          onClick={() => like(!liked)}
          className={ACTION}
          variant="bare"
        >
          {/* 좋아요 색은 아이콘에 직접 — 원(ACTION_ICON)의 PC 글자색과 겹치지 않게 */}
          <span ref={heart} className={ACTION_ICON}>
            <PiHeartFill
              className={cn(
                ACTION_GLYPH,
                'transition-colors duration-200',
                liked && 'text-coral'
              )}
            />
          </span>
          <span aria-hidden="true">{kfmt(stats.likes)}</span>
        </Button>
        {/* 말풍선 = 댓글(쇼츠와 같다, 2026-10-08) — PC 는 카드 오른쪽 패널, 모바일은 바텀시트. 숫자는 댓글 수 */}
        <Button
          type="button"
          aria-label={commentCount === null ? '댓글' : `댓글 ${commentCount}개`}
          aria-pressed={commentsOpen}
          onClick={() => onComments(id)}
          className={ACTION}
          variant="bare"
        >
          <span className={ACTION_ICON}>
            <ChatIcon className={cn(ACTION_GLYPH, ACTION_SOFT)} />
          </span>
          <span aria-hidden="true">
            {commentCount === null ? '댓글' : kfmt(commentCount)}
          </span>
        </Button>
        <Button
          type="button"
          aria-label="링크 공유"
          onClick={() => shareLink(`/explore?c=${id}`)}
          className={ACTION}
          variant="bare"
        >
          <span className={ACTION_ICON}>
            <ShareIcon className={cn(ACTION_GLYPH, ACTION_SOFT)} />
          </span>
          <span aria-hidden="true">공유</span>
        </Button>
        {/* 상세 — 릴스의 오디오 칩 자리. 작품 썸네일을 눌러 소개 화면으로 */}
        {/* 위 공유와 한 묶음처럼 붙어 보였다(2026-10-08) — 썸네일은 버튼이 아니라 '작품 카드'라 간격을 한 칸 더(16 + 12px) */}
        {/* 썸네일은 바깥 흰 테두리(2px 그림자)가 상자 밖으로 나와 글자가 그 위에 얹혀 보였다(2026-10-08 모바일) — 테두리만큼 띄운다(mb-1) */}
        <Link
          href={to(`/c/${id}`)}
          aria-label={`${c.name} 상세 보기`}
          className={cn(ACTION, 'mt-3')}
        >
          <span className="relative mb-1 size-10 flex-none overflow-hidden rounded-field shadow-[0_0_0_2px_rgb(255_255_255/0.9),0_2px_10px_rgb(0_0_0/0.5)] transition-[scale] duration-200 ease-smooth group-active:scale-90">
            <SafeImage
              src={thumb(id)}
              alt=""
              fill
              sizes="40px"
              className="object-cover"
              appearance="plain"
            />
          </span>
          <span aria-hidden="true">상세</span>
        </Link>
      </div>
    </section>
  )
}
